import { useV2CurrentAccount } from '@ferreri/minato-auth-react';
import {
  createPersistedStore,
  createQueryStore,
} from '@ferreri/minato-react-utils';
import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { findGeocode } from './geolocation.client';

type GeolocationPersisted = {
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
};

type Coords = {
  latitude: number;
  longitude: number;
};

type Options = {
  coords?: Coords;
  address?: string;
};

const SESSION_GEOLOCATION_KEY = '@Angeplus::geolocation';

export const usePersistedGeolocation = () => {
  const [value, setValue] = createPersistedStore<GeolocationPersisted>(
    SESSION_GEOLOCATION_KEY,
  );

  const hasCurrentGeolocation =
    value?.latitude && value?.latitude && value?.address;

  return { value, setValue, hasCurrentGeolocation };
};

export const useGeocode = (options: Options) => {
  const params = {
    latitude: options.coords?.latitude,
    longitude: options.coords?.longitude,
    address: options.address,
  };

  return createQueryStore({
    queryKey: ['place', options],
    queryFn: () => findGeocode(params),
    enabled: (!!params.latitude && !!params.longitude) || !!params.address,
  });
};

export const useDebouncedGeocode = (options: Options) => {
  const [stableParams, setStableParams] = useState(null);

  const params = {
    latitude: options.coords?.latitude,
    longitude: options.coords?.longitude,
    address: options.address,
  };

  useEffect(() => {
    const debouncedUpdate = debounce(() => {
      setStableParams(params);
    }, 500);

    debouncedUpdate();

    return () => debouncedUpdate.cancel();
  }, [params]);

  return createQueryStore({
    queryKey: ['place', stableParams],
    queryFn: () => findGeocode(stableParams),
    enabled:
      (!!stableParams?.latitude && !!stableParams?.longitude) ||
      !!stableParams?.address,
  });
};

export const useCurrentGeolocation = (options = {}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentCoords, setCurrentCoords] = useState(null);
  const optionsRef = useRef(options);
  const isError = Boolean(error);

  useEffect(() => {
    if (!currentCoords && isLoading) {
      navigator.geolocation.getCurrentPosition(
        onEvent,
        onEventError,
        optionsRef.current,
      );
    }
  }, [currentCoords, isLoading]);

  const onEvent = ({ coords, timestamp }) => {
    setCurrentCoords({
      timestamp,
      latitude: coords.latitude,
      longitude: coords.longitude,
      altitude: coords.altitude,
      accuracy: coords.accuracy,
      altitudeAccuracy: coords.altitudeAccuracy,
      heading: coords.heading,
      speed: coords.speed,
    });
    setIsLoading(false);
  };

  const onEventError = (error) => {
    setError(error);
    setIsLoading(false);
  };

  return { data: currentCoords, isLoading, isError };
};

export const useGeocodeCombinedCurrentGeolocation = () => {
  const queryCurrentGeolocation = useCurrentGeolocation();

  const queryGeocode = useGeocode({
    coords: {
      latitude: queryCurrentGeolocation.data?.latitude,
      longitude: queryCurrentGeolocation.data?.longitude,
    },
  });
  const data = { ...queryGeocode.data, ...queryCurrentGeolocation.data };
  const isLoading = queryCurrentGeolocation.isLoading || queryGeocode.isLoading;
  const isError = queryCurrentGeolocation.isError || queryGeocode.isError;

  return {
    ...queryGeocode,
    data,
    isLoading,
    isError,
  };
};

export const useCurrentAccountGeocode = () => {
  const { data: account, isLoading: isLoadingCurrentAccount } =
    useV2CurrentAccount();
  const hasNoAddress =
    !account?.home_address?.street ||
    !account?.home_address?.neighborhood ||
    !account?.home_address?.city ||
    !account?.home_address?.state;
  const addressLine = `${account?.home_address?.street}, ${account?.home_address?.neighborhood}, ${account?.home_address?.city} - ${account?.home_address?.state}`;
  const formattedAddress = `${account?.home_address?.street}, ${account?.home_address?.neighborhood}, ${account?.home_address?.city}, ${account?.home_address?.state}`;
  const queryGeocode = useGeocode({
    address: formattedAddress,
  });
  const data = hasNoAddress
    ? null
    : { ...queryGeocode.data, addressLine, formattedAddress };
  const isLoading = isLoadingCurrentAccount || queryGeocode.isLoading;

  return { ...queryGeocode, data, isLoading };
};
